import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    Chip,
    Alert,
    LinearProgress,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    LocalShipping as ShippingIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    Assignment as AssignmentIcon,
    TrendingUp as TrendingUpIcon,
    Inventory as InventoryIcon,
    ShowChart as ShowChartIcon,
    Print as PrintIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Payments as PaymentsIcon,
} from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
    procurementService,
    Purchase,
    PurchasePayment,
    PurchasePaymentCreate,
} from "../../services/procurementService";
import { formatCurrencyBDT } from "../../utils/currency";
import { AddPurchasePaymentDialog } from "../../components/procurement/AddPurchasePaymentDialog";
import { ProductThumbnail } from "../../components/common/ProductThumbnail";
import { compareByProductName } from "../../utils/procurementSort";
import {
    PurchaseOrderPDFGenerator,
    DEFAULT_PURCHASE_ORDER_COLUMNS,
    DEFAULT_HEADER_FIELDS,
    ColumnConfig,
    HeaderFieldConfig,
} from "../../utils/pdfGenerator";
import { getPurchaseOrderCompanyInfo } from "../../utils/purchaseOrderPdf/companyInfo";
import PrintCustomizationDialog from "../../components/PrintCustomizationDialog";
import { useAccounts } from "../../hooks/useAccounts";

const ViewPurchaseOrder: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    // Phones get stacked rows — the item and payment tables need ~700px.
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const location = useLocation();
    const backTo =
        (location.state as { from?: string } | null)?.from ||
        "/procurement/purchases";
    const { id } = useParams<{ id: string }>();
    const { getAccountName } = useAccounts();
    const [loading, setLoading] = useState(true);
    const [purchaseData, setPurchaseData] = useState<Purchase | null>(null);
    const [error, setError] = useState<string>("");
    const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    // Payment state
    const [payments, setPayments] = useState<PurchasePayment[]>([]);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError("");
            const [purchase, pmts] = await Promise.all([
                procurementService.getPurchase(id!),
                procurementService.listPurchasePayments(id!),
            ]);
            setPurchaseData(purchase);
            setPayments(pmts);
        } catch (error) {
            console.error("Error loading purchase order:", error);
            setError("Failed to load purchase order details");
        } finally {
            setLoading(false);
        }
    };

    // ── Payment handlers ──────────────────────────────────────────────────
    const netPayable = purchaseData
        ? Number(purchaseData.total_amount) -
          Number(purchaseData.discount_amount) +
          Number(purchaseData.tax_amount) +
          Number(purchaseData.shipping_cost)
        : 0;

    const amountPaid = payments.reduce((s, p) => s + Number(p.amount), 0);
    const amountDue = Math.max(0, netPayable - amountPaid);

    const paymentStatusColor = (s: string) =>
        s === "PAID" ? "success" : s === "PARTIAL" ? "warning" : "error";

    const handleAddPayment = async (data: PurchasePaymentCreate) => {
        const created = await procurementService.createPurchasePayment(id!, data);
        setPayments((prev) => [...prev, created]);
        // Refresh purchase to get updated payment_status
        const updated = await procurementService.getPurchase(id!);
        setPurchaseData(updated);
    };

    const handleDeletePayment = async (paymentId: string) => {
        if (!window.confirm("Remove this payment record?")) return;
        try {
            await procurementService.deletePurchasePayment(id!, paymentId);
            setPayments((prev) => prev.filter((p) => p.payment_id !== paymentId));
            const updated = await procurementService.getPurchase(id!);
            setPurchaseData(updated);
        } catch {
            // silently ignore — user will see no change
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "PENDING":
                return "warning";
            case "CONFIRMED":
                return "info";
            case "RECEIVED":
                return "success";
            case "PARTIAL_RECEIVED":
                return "primary";
            case "CANCELLED":
                return "error";
            case "ON_HOLD":
                return "default";
            default:
                return "default";
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case "PENDING":
                return <ScheduleIcon fontSize="small" />;
            case "CONFIRMED":
                return <AssignmentIcon fontSize="small" />;
            case "RECEIVED":
                return <CheckCircleIcon fontSize="small" />;
            case "PARTIAL_RECEIVED":
                return <ShippingIcon fontSize="small" />;
            default:
                return <AssignmentIcon fontSize="small" />;
        }
    };

    const calculateStatistics = () => {
        if (!purchaseData) return null;

        const totalItems = purchaseData.items.length;
        const totalQuantityOrdered = purchaseData.items.reduce(
            (sum, item) => sum + item.quantity_ordered,
            0,
        );
        const totalQuantityReceived = purchaseData.items.reduce(
            (sum, item) => sum + item.quantity_received,
            0,
        );
        const fulfillmentRate =
            totalQuantityOrdered > 0
                ? (totalQuantityReceived / totalQuantityOrdered) * 100
                : 0;

        let totalPotentialRevenue = 0;
        let totalCost = Number(purchaseData.total_amount);
        let itemsWithPricing = 0;

        purchaseData.items.forEach((item) => {
            if (item.product?.selling_price) {
                totalPotentialRevenue +=
                    item.quantity_ordered * Number(item.product.selling_price);
                itemsWithPricing++;
            }
        });

        const expectedProfitMargin =
            totalPotentialRevenue > 0
                ? ((totalPotentialRevenue - totalCost) /
                      totalPotentialRevenue) *
                  100
                : 0;
        const expectedProfit = totalPotentialRevenue - totalCost;

        const averageCostPerItem =
            totalQuantityOrdered > 0 ? totalCost / totalQuantityOrdered : 0;

        return {
            totalItems,
            totalQuantityOrdered,
            totalQuantityReceived,
            fulfillmentRate,
            totalPotentialRevenue,
            expectedProfitMargin,
            expectedProfit,
            averageCostPerItem,
            itemsWithPricing,
        };
    };

    const stats = calculateStatistics();

    const handlePrint = async (
        customizedColumns: ColumnConfig[],
        headerFields: HeaderFieldConfig[],
        includeShipping: boolean,
        showSummary: boolean,
    ) => {
        if (!purchaseData) return;

        try {
            setIsGeneratingPDF(true);
            const companyInfo = await getPurchaseOrderCompanyInfo();
            const pdfGenerator = new PurchaseOrderPDFGenerator(companyInfo);
            await pdfGenerator.download(
                purchaseData,
                customizedColumns,
                headerFields,
                includeShipping,
                showSummary,
            );
        } catch (error) {
            console.error("Error generating PDF:", error);
            setError("Failed to generate PDF");
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const handlePreview = async (
        customizedColumns: ColumnConfig[],
        headerFields: HeaderFieldConfig[],
        includeShipping: boolean,
        showSummary: boolean,
    ) => {
        if (!purchaseData) return;

        try {
            setIsGeneratingPDF(true);
            const companyInfo = await getPurchaseOrderCompanyInfo();
            const pdfGenerator = new PurchaseOrderPDFGenerator(companyInfo);
            const pdfDataUrl = await pdfGenerator.preview(
                purchaseData,
                customizedColumns,
                headerFields,
                includeShipping,
                showSummary,
            );
            window.open(pdfDataUrl, "_blank");
        } catch (error) {
            console.error("Error previewing PDF:", error);
            setError("Failed to preview PDF");
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "400px",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error || !purchaseData) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || "Purchase order not found"}
                </Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(backTo)}
                >
                    Back to Purchase Orders
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 0, sm: 3 } }}>
            {/* Header */}
            <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                mb={3}
                className="no-print"
                sx={{ flexWrap: "wrap", gap: 2 }}
            >
                <Box display="flex" alignItems="center">
                    <IconButton
                        onClick={() => navigate(backTo)}
                        sx={{ mr: 1 }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontSize: { xs: "1.35rem", sm: "2.125rem" } }}
                    >
                        Purchase Order Details
                    </Typography>
                </Box>
                <Box display="flex" gap={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={() => setIsPrintDialogOpen(true)}
                        disabled={isGeneratingPDF}
                    >
                        {isGeneratingPDF ? "Generating..." : "Print"}
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() =>
                            navigate(
                                `/procurement/purchases/${purchaseData.purchase_id}/edit`,
                                { state: { from: backTo } },
                            )
                        }
                    >
                        Edit
                    </Button>
                </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Purchase Order Header */}
                <Card>
                    <CardContent>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            flexWrap="wrap"
                            gap={1.5}
                            mb={{ xs: 1, sm: 3 }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="h5"
                                    fontWeight="bold"
                                    gutterBottom
                                    sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" }, overflowWrap: "anywhere" }}
                                >
                                    {purchaseData.purchase_number}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    Purchase Date:{" "}
                                    {new Date(
                                        purchaseData.purchase_date,
                                    ).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                    })}
                                </Typography>
                                {purchaseData.reference_number && (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Reference:{" "}
                                        {purchaseData.reference_number}
                                    </Typography>
                                )}
                            </Box>
                            <Chip
                                icon={getStatusIcon(purchaseData.status)}
                                label={purchaseData.status.replace("_", " ")}
                                color={
                                    getStatusColor(purchaseData.status) as any
                                }
                                size="medium"
                                sx={{
                                    fontWeight: "bold",
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                    py: { xs: 2, sm: 2.5 },
                                }}
                            />
                        </Box>
                    </CardContent>
                </Card>

                {/* Statistics Section */}
                {stats && (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "1fr 1fr",
                                lg: "repeat(4, 1fr)",
                            },
                            gap: 2,
                        }}
                    >
                        {/* Expected Profit Margin */}
                        <Card
                            sx={{
                                bgcolor:
                                    stats.expectedProfitMargin > 0
                                        ? "success.light"
                                        : "error.light",
                                color:
                                    stats.expectedProfitMargin > 0
                                        ? "success.dark"
                                        : "error.dark",
                            }}
                        >
                            <CardContent>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <TrendingUpIcon />
                                    <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                    >
                                        Expected Profit Margin
                                    </Typography>
                                </Box>
                                <Typography variant="h4" fontWeight="bold">
                                    {stats.expectedProfitMargin.toFixed(2)}%
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ opacity: 0.8 }}
                                >
                                    {formatCurrencyBDT(stats.expectedProfit)}{" "}
                                    profit
                                </Typography>
                                {stats.itemsWithPricing < stats.totalItems && (
                                    <Typography
                                        variant="caption"
                                        display="block"
                                        sx={{ mt: 0.5, opacity: 0.7 }}
                                    >
                                        Based on {stats.itemsWithPricing}/
                                        {stats.totalItems} items with pricing
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>

                        {/* Fulfillment Rate */}
                        <Card
                            sx={{ bgcolor: "info.light", color: "info.dark" }}
                        >
                            <CardContent>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <CheckCircleIcon />
                                    <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                    >
                                        Fulfillment Rate
                                    </Typography>
                                </Box>
                                <Typography variant="h4" fontWeight="bold">
                                    {stats.fulfillmentRate.toFixed(1)}%
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ opacity: 0.8 }}
                                >
                                    {stats.totalQuantityReceived} of{" "}
                                    {stats.totalQuantityOrdered} units received
                                </Typography>
                            </CardContent>
                        </Card>

                        {/* Total Items */}
                        <Card
                            sx={{
                                bgcolor: "primary.light",
                                color: "primary.dark",
                            }}
                        >
                            <CardContent>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <InventoryIcon />
                                    <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                    >
                                        Total Items
                                    </Typography>
                                </Box>
                                <Typography variant="h4" fontWeight="bold">
                                    {stats.totalItems}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ opacity: 0.8 }}
                                >
                                    {stats.totalQuantityOrdered} total units
                                </Typography>
                            </CardContent>
                        </Card>

                        {/* Potential Revenue */}
                        <Card
                            sx={{
                                bgcolor: "warning.light",
                                color: "warning.dark",
                            }}
                        >
                            <CardContent>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <ShowChartIcon />
                                    <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                    >
                                        Potential Revenue
                                    </Typography>
                                </Box>
                                <Typography variant="h5" fontWeight="bold">
                                    {formatCurrencyBDT(
                                        stats.totalPotentialRevenue,
                                    )}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ opacity: 0.8 }}
                                >
                                    Avg:{" "}
                                    {formatCurrencyBDT(
                                        stats.averageCostPerItem,
                                    )}
                                    /unit cost
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {/* Supplier and Delivery Information */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 3,
                    }}
                >
                    <Card>
                        <CardContent>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <AssignmentIcon color="primary" />
                                Supplier Information
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {purchaseData.supplier && (
                                <Box>
                                    <Typography
                                        variant="body1"
                                        fontWeight="bold"
                                        gutterBottom
                                    >
                                        {purchaseData.supplier.name}
                                    </Typography>
                                    {purchaseData.supplier.contact_person && (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            Contact:{" "}
                                            {
                                                purchaseData.supplier
                                                    .contact_person
                                            }
                                        </Typography>
                                    )}
                                    {purchaseData.supplier.email && (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            Email: {purchaseData.supplier.email}
                                        </Typography>
                                    )}
                                    {purchaseData.supplier.phone && (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            Phone: {purchaseData.supplier.phone}
                                        </Typography>
                                    )}
                                    {purchaseData.supplier.address && (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            sx={{ mt: 1 }}
                                        >
                                            Address:{" "}
                                            {purchaseData.supplier.address}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <ShippingIcon color="primary" />
                                Delivery & Payment
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Expected Delivery:</strong>{" "}
                                    {purchaseData.expected_delivery_date
                                        ? new Date(
                                              purchaseData.expected_delivery_date,
                                          ).toLocaleDateString("en-US", {
                                              year: "numeric",
                                              month: "long",
                                              day: "numeric",
                                          })
                                        : "Not specified"}
                                </Typography>
                                {purchaseData.received_date && (
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Received Date:</strong>{" "}
                                        {new Date(
                                            purchaseData.received_date,
                                        ).toLocaleDateString("en-US", {
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                        })}
                                    </Typography>
                                )}
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Payment Terms:</strong>{" "}
                                    {purchaseData.payment_terms ||
                                        "Not specified"}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Shipping Cost:</strong>{" "}
                                    {formatCurrencyBDT(
                                        Number(purchaseData.shipping_cost),
                                    )}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* ── Payment Tracking ───────────────────────────────────── */}
                <Card>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <PaymentsIcon color="primary" />
                                Payment Tracking
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                {purchaseData.payment_status && (
                                    <Chip
                                        label={purchaseData.payment_status}
                                        color={paymentStatusColor(purchaseData.payment_status) as any}
                                        size="small"
                                    />
                                )}
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setPaymentDialogOpen(true)}
                                    disabled={amountDue <= 0}
                                >
                                    Record Payment
                                </Button>
                            </Box>
                        </Box>
                        <Divider sx={{ mb: 2 }} />

                        {/* Progress bar */}
                        <Box mb={2}>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    Paid: {formatCurrencyBDT(amountPaid)} of {formatCurrencyBDT(netPayable)}
                                </Typography>
                                <Typography variant="caption" color={amountDue > 0 ? "error.main" : "success.main"}>
                                    {amountDue > 0 ? `Due: ${formatCurrencyBDT(amountDue)}` : "Fully Paid"}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={netPayable > 0 ? Math.min(100, (amountPaid / netPayable) * 100) : 0}
                                color={amountDue <= 0 ? "success" : "primary"}
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                        </Box>

                        {/* Payment list */}
                        {payments.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No payments recorded yet.
                            </Typography>
                        ) : isMobile ? (
                            <Paper variant="outlined">
                                {payments.map((p, i) => (
                                    <Box
                                        key={p.payment_id}
                                        sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.25, borderTop: i ? 1 : 0, borderColor: "divider" }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                                <Typography variant="body2" fontWeight={700}>
                                                    {formatCurrencyBDT(Number(p.amount))}
                                                </Typography>
                                                <Chip label={p.payment_method} size="small" variant="outlined" />
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                {new Date(p.payment_date).toLocaleDateString()}
                                                {getAccountName(p.account_id) ? ` · ${getAccountName(p.account_id)}` : ""}
                                                {p.reference_number ? ` · Ref ${p.reference_number}` : ""}
                                            </Typography>
                                        </Box>
                                        <IconButton
                                            color="error"
                                            onClick={() => handleDeletePayment(p.payment_id)}
                                            aria-label="Delete payment"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Paper>
                        ) : (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "action.hover" }}>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Method</TableCell>
                                            <TableCell>Reference</TableCell>
                                            <TableCell>Account</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                            <TableCell align="center">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {payments.map((p) => (
                                            <TableRow key={p.payment_id}>
                                                <TableCell>
                                                    {new Date(p.payment_date).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={p.payment_method} size="small" variant="outlined" />
                                                </TableCell>
                                                <TableCell>{p.reference_number || "—"}</TableCell>
                                                <TableCell>{getAccountName(p.account_id) || "—"}</TableCell>
                                                <TableCell align="right">
                                                    <strong>{formatCurrencyBDT(Number(p.amount))}</strong>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeletePayment(p.payment_id)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>

                {/* ── Add Payment Dialog ──────────────────────────────────── */}
                <AddPurchasePaymentDialog
                    open={paymentDialogOpen}
                    onClose={() => setPaymentDialogOpen(false)}
                    onConfirm={handleAddPayment}
                    amountDue={amountDue}
                />

                {/* Purchase Items */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Purchase Items
                        </Typography>
                        {isMobile ? (
                            <Paper variant="outlined">
                                {[...purchaseData.items].sort(compareByProductName).map((item, index) => {
                                    const remaining = item.quantity_remaining || 0;
                                    return (
                                        <Box key={index} sx={{ p: 1.5, borderTop: index ? 1 : 0, borderColor: "divider" }}>
                                            <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                                                <ProductThumbnail images={item.product?.images} alt={item.product?.name || ""} />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {item.product?.name || "N/A"}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                                        {item.product?.sku || "N/A"}
                                                    </Typography>
                                                    {item.notes && (
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            Note: {item.notes}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                                                    <Typography variant="body2" fontWeight={600} color="primary">
                                                        {formatCurrencyBDT(Number(item.total_cost))}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatCurrencyBDT(Number(item.unit_cost))} each
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1, pl: 6.5 }}>
                                                <Chip label={`Ordered ${item.quantity_ordered}`} size="small" variant="outlined" />
                                                <Chip
                                                    label={`Received ${item.quantity_received}`}
                                                    size="small"
                                                    variant="outlined"
                                                    color={
                                                        item.quantity_received === item.quantity_ordered
                                                            ? "success"
                                                            : item.quantity_received > 0
                                                              ? "primary"
                                                              : "default"
                                                    }
                                                />
                                                {remaining > 0 && (
                                                    <Chip label={`${remaining} to come`} size="small" color="warning" variant="outlined" />
                                                )}
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Paper>
                        ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "action.hover" }}>
                                        <TableCell>
                                            <strong>Product</strong>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>SKU</strong>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>Qty Ordered</strong>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>Qty Received</strong>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>Qty Remaining</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>Unit Cost</strong>
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>Total Cost</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[...purchaseData.items]
                                        .sort(compareByProductName)
                                        .map((item, index) => (
                                        <TableRow
                                            key={index}
                                            sx={{
                                                "&:hover": {
                                                    bgcolor: "action.hover",
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1.5,
                                                    }}
                                                >
                                                    <ProductThumbnail
                                                        images={item.product?.images}
                                                        alt={item.product?.name || ""}
                                                    />
                                                    <Box>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="medium"
                                                        >
                                                            {item.product?.name ||
                                                                "N/A"}
                                                        </Typography>
                                                        {item.notes && (
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                                display="block"
                                                            >
                                                                Note: {item.notes}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{
                                                        fontFamily: "monospace",
                                                        bgcolor: "action.hover",
                                                        px: 1,
                                                        py: 0.5,
                                                        borderRadius: 1,
                                                        display: "inline-block",
                                                    }}
                                                >
                                                    {item.product?.sku || "N/A"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2">
                                                    {item.quantity_ordered}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={
                                                        item.quantity_received
                                                    }
                                                    size="small"
                                                    color={
                                                        item.quantity_received ===
                                                        item.quantity_ordered
                                                            ? "success"
                                                            : item.quantity_received >
                                                                0
                                                              ? "primary"
                                                              : "default"
                                                    }
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography
                                                    variant="body2"
                                                    color={
                                                        (item.quantity_remaining ||
                                                            0) > 0
                                                            ? "warning.main"
                                                            : "textSecondary"
                                                    }
                                                    fontWeight={
                                                        (item.quantity_remaining ||
                                                            0) > 0
                                                            ? "bold"
                                                            : "normal"
                                                    }
                                                >
                                                    {item.quantity_remaining ||
                                                        0}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2">
                                                    {formatCurrencyBDT(
                                                        Number(item.unit_cost),
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                    color="primary"
                                                >
                                                    {formatCurrencyBDT(
                                                        Number(item.total_cost),
                                                    )}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        )}

                        {/* Order Summary */}
                        <Box
                            sx={{
                                mt: 3,
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Box sx={{ minWidth: { xs: "100%", sm: 350 } }}>
                                {/* Calculate received total */}
                                {(() => {
                                    const receivedSubtotal =
                                        purchaseData.items.reduce(
                                            (sum, item) =>
                                                sum +
                                                Number(item.quantity_received) *
                                                    Number(item.unit_cost),
                                            0,
                                        );
                                    // total_amount is the pure item subtotal
                                    // (shipping/discount apply on top, not
                                    // folded in) — see netPayable above.
                                    const orderedSubtotal = Number(
                                        purchaseData.total_amount,
                                    );
                                    const isPartiallyReceived =
                                        receivedSubtotal < orderedSubtotal &&
                                        receivedSubtotal > 0;

                                    return (
                                        <>
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                mb={1}
                                            >
                                                <Typography>
                                                    Subtotal (Ordered):
                                                </Typography>
                                                <Typography>
                                                    {formatCurrencyBDT(
                                                        orderedSubtotal,
                                                    )}
                                                </Typography>
                                            </Box>
                                            {isPartiallyReceived && (
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    mb={1}
                                                >
                                                    <Typography color="primary">
                                                        Subtotal (Received):
                                                    </Typography>
                                                    <Typography
                                                        color="primary"
                                                        fontWeight="medium"
                                                    >
                                                        {formatCurrencyBDT(
                                                            receivedSubtotal,
                                                        )}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {Number(
                                                purchaseData.discount_amount,
                                            ) > 0 && (
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    mb={1}
                                                >
                                                    <Typography>
                                                        Discount:
                                                    </Typography>
                                                    <Typography color="success.main">
                                                        -
                                                        {formatCurrencyBDT(
                                                            Number(
                                                                purchaseData.discount_amount,
                                                            ),
                                                        )}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {Number(purchaseData.tax_amount) >
                                                0 && (
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    mb={1}
                                                >
                                                    <Typography>
                                                        Tax:
                                                    </Typography>
                                                    <Typography>
                                                        {formatCurrencyBDT(
                                                            Number(
                                                                purchaseData.tax_amount,
                                                            ),
                                                        )}
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                mb={1}
                                            >
                                                <Typography>
                                                    Shipping:
                                                </Typography>
                                                <Typography>
                                                    {formatCurrencyBDT(
                                                        Number(
                                                            purchaseData.shipping_cost,
                                                        ),
                                                    )}
                                                </Typography>
                                            </Box>
                                            <Divider sx={{ my: 1.5 }} />
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                mb={isPartiallyReceived ? 1 : 0}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                >
                                                    Total (Ordered):
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    color="primary"
                                                >
                                                    {formatCurrencyBDT(
                                                        netPayable,
                                                    )}
                                                </Typography>
                                            </Box>
                                            {isPartiallyReceived && (
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                >
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight="bold"
                                                        color="success.main"
                                                    >
                                                        Total (Received):
                                                    </Typography>
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight="bold"
                                                        color="success.main"
                                                    >
                                                        {formatCurrencyBDT(
                                                            receivedSubtotal +
                                                                Number(
                                                                    purchaseData.shipping_cost,
                                                                ) -
                                                                Number(
                                                                    purchaseData.discount_amount,
                                                                ) +
                                                                Number(
                                                                    purchaseData.tax_amount,
                                                                ),
                                                        )}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </>
                                    );
                                })()}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* Notes */}
                {purchaseData.notes && (
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Notes
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{
                                    whiteSpace: "pre-wrap",
                                    bgcolor: "action.hover",
                                    p: 2,
                                    borderRadius: 1,
                                }}
                            >
                                {purchaseData.notes}
                            </Typography>
                        </CardContent>
                    </Card>
                )}
            </Box>

            {/* Print Customization Dialog */}
            <PrintCustomizationDialog
                open={isPrintDialogOpen}
                onClose={() => setIsPrintDialogOpen(false)}
                columns={DEFAULT_PURCHASE_ORDER_COLUMNS}
                headerFields={DEFAULT_HEADER_FIELDS}
                onPrint={handlePrint}
                onPreview={handlePreview}
            />

            {/* Print Styles */}
            <style>
                {`
                    @media print {
                        .no-print {
                            display: none !important;
                        }
                        body {
                            print-color-adjust: exact;
                            -webkit-print-color-adjust: exact;
                        }
                    }
                `}
            </style>
        </Box>
    );
};

export default ViewPurchaseOrder;
